import { Fragment, useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import SEO from '../components/SEO';
import styles from '../styles/Home.module.css';

import FeatureCard from '../components/home/FeatureCard';
import WhyCard from '../components/home/WhyCard';
import language from '../languages/index.json';

import { features, whyCards, testimonials, companies, productFeatures, usersCard, partners, homeFaqs } from '../const/home';
import { news } from '../const/media';
import { awards, mailFormat } from '../const/shared';
import { questions } from '../const/faq';
import ContactUsCTA from '../components/ContactUsCTA'
import { ChevronRightIcon } from '../components/icons/Icon'

const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), {
  ssr: false,
});

export default function Home({ locale }) {
  const [testimonial, setTestimonial] = useState({});
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const recaptchaRef = useRef();
  const scrollRef = useRef();
  const tryProductRef=useRef(null);
  const scrollCompaniesRef=useRef(null);

  const selectTestimonial = (id) =>
    setTestimonial(testimonials.filter((t) => t.id === id)[0]);

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    setValid(mailFormat.test(e.target.value));
  };

  const scroll = (direction) => {
    const container = scrollRef.current;
    const cardWidth = container.querySelector(`.${styles.blogItem}`).offsetWidth + 20; // Include margin/padding

    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    recaptchaRef.current.execute();
  };

  const QuestionSection = ({keyIndex, que, ans}) => {
    const [isOpen, setIsOpen] = useState(false)

    return(
        <div className={styles.homeFaqItem} onClick={()=>setIsOpen(!isOpen)}>
          <div className={styles.homeFaqQuestion}>
              <p>{que}</p>
              <span className={styles.homeFaqToggle}>
                <Image src={isOpen? '/img/shrink-icon.svg': '/img/expand-icon.svg'} alt="FAQ" width={28} height={28} objectFit='contain'/>
              </span>
          </div>
          <div className={`${styles.homeFaqAnswer} ${isOpen ? styles.homeFaqOpen : ""}`}>
              {ans.map((item, index)=><p key={index}>{item}</p>)}
          </div>
        </div>
    )
  }

  const handleRecaptchaChange = async (captcha) => {
    if (!captcha) return;

    setLoading(true);

    try {
      const response = await fetch('/api/request', {
        method: 'POST',
        body: JSON.stringify({ email, captcha }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const error = await response.json();
        throw new Error(error.message);
      }
    } catch (error) {
      setError(error?.message || 'Something went wrong');
    } finally {
      recaptchaRef.current.reset();
      setEmail('');
      setValid(false);
      setLoading(false);
      setTimeout(() => {
        setError(false);
        setSuccess(false);
        setShowModal(false);
      }, 3000);
    }
  };

  const onClickGoToProduct = () => {
    if (tryProductRef.current) {
      tryProductRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); // 'smooth' for animated scroll
    }
  };

  const init = async () => {
    setTestimonial(testimonials[0]);
    const res = await fetch(`/api/posts`);
    const data = await res.json();
    setPosts(data.posts);
  };
  useEffect(() => init(), []);

  return (
    <div className={styles.container}>
      <SEO
        title={language['seo title'][locale]}
        description={language['seo desc'][locale]}
        image='https://inspektlabs.com/img/car.png'
        url='https://inspektlabs.com'
        page=''
      />

      {/* Preload the hero poster (LCP element) so the browser fetches it
          immediately at high priority — fixes Lighthouse "LCP request
          discovery" (fetchpriority=high). */}
      <Head>
        <link
          rel='preload'
          as='image'
          href='/img/home-poster.jpg'
          fetchpriority='high'
        />
      </Head>

      {/* Main Body */}
      <main className={styles.main}>
        {/* Fold 1 — Hero */}
        <section className={styles.featured}>
          <video
            autoPlay
            muted
            loop
            preload='auto'
            playsInline
            poster='/img/home-poster.jpg'
            className={styles.myVideo}
          >
            <source src='/img/home.webm' type='video/webm' />
            <source src='/img/home.mp4' type='video/mp4' />
            Your browser does not support the video tag.
          </video>
          <div className={styles.grid}>
            <div className={styles.gridItem}>
              <span className={styles.heroEyebrow}>AI-Powered Vehicle Inspections</span>
              <h1 className={styles.featuredTitle}>
                Automate Vehicle Inspections <span className={styles.titleHighlights}>with AI</span>
              </h1>
              <p className={styles.heroSubheading}>
                Turn vehicle photos and videos into structured inspection results in seconds.
              </p>
              <p className={styles.featuredText}>
                {language['featureText'][locale]}
              </p>
              <div className={styles.btnContainer}>
                <ContactUsCTA as="div" cluster = "homepage" className={styles.mainBtn}>
                  Request a Demo
                </ContactUsCTA>
                <Link href="/our-core-technology">
                  <a className={styles.heroSecondaryBtn}>Explore the Platform →</a>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust / proof — companies */}
        <section className={styles.companies}>
          <h2 className={styles.trustedBy}>Trusted by Leading Insurance & Automotive Brands</h2>
          <p className={styles.companiesSubtitle}>
            Supporting vehicle inspection workflows across insurance and automotive operations.
          </p>

          <div className={styles.companiesWrapper}>
          <div className={styles.companiesContainer} ref={scrollCompaniesRef}>
            {[...companies, ...companies].map((company, idx)=>(
              <div key={idx} className={styles.companyIcon}>
                <Image src={`/img/${company.img}`} width={company.width} height={company.height} alt={company.title} />
              </div>
            ))}
          </div>
          </div>
        </section>

        {/* Fold 2 — Problem + Platform / Capabilities */}
        <section className={styles.productsSection}>
            <div className={styles.products} ref={tryProductRef}>
              <h2 className={styles.productsTitle}>
                Automate Vehicle Inspections Without Adding Complexity
              </h2>
              <p className={styles.productsText}>
                Traditional inspections can involve manual checks, inconsistent documentation and time-consuming reports. Inspektlabs uses AI to analyse vehicle images and videos, turning inspection inputs into structured findings that teams can review and use in their existing workflows.
              </p>
              <h3 className={styles.productsSubheading}>
                Everything You Need to Automate Vehicle Inspections
              </h3>
              <p className={styles.productsText}>
                From damage detection to structured reporting, Inspektlabs supports the key steps involved in digital vehicle inspection.
              </p>
              <div className={styles.productsContainer}>
                <div className={styles.productFeatureList}>
                  {productFeatures.map((feature, index) => {
                    const isOpen = openIndex === index;
                    return (
                      <>
                        <div key={index} className={`${styles.productFeature} ${isOpen? styles.activeDiv:""}`} onClick={()=>{if(openIndex===index){setOpenIndex(null);}else{setOpenIndex(index);setSelectedIndex(index);}}}>
                          <Image src={`/img/${feature.img}`} alt={feature.title} width={54} height={54} />
                          <div className={styles.productFeatureContent}>
                            <div className={styles.productFeatureTitleContainer}>
                              <h3 className={`${styles.productFeatureTitle} ${isOpen ? styles.activeTitle: ""}`}>{feature.title}</h3>
                              <Image src="/img/downArrorw.svg" alt="FAQ" width={24} height={24}  objectFit='contain'/>
                            </div>
                            <div className={styles.innerImg}>
                              {isOpen && <Image src={`/img/${productFeatures[index].mainImg}`} alt='AI-powered Vehicle Inspection Solution' width={575} height={700} />}
                            </div>
                            <div className={`${styles.productFeatureTextWrapper} ${isOpen ? styles.open : ''}`}>
                              {isOpen && <p className={styles.productFeatureText}>{feature.text}</p>}
                              {isOpen && (
                                  feature.link ? (
                                    <Link href={feature.link} target="_blank" rel="noreferrer">
                                      <a className={`${isOpen ? styles.activeTitle : ""}`}>
                                        {feature.buttonText}
                                        <Image src="/img/arrow.svg" alt="arrow" width={50} height={10} />
                                      </a>
                                    </Link>
                                  ) : (
                                    <p className={styles.comingSoon}>Coming soon</p>
                                  )
                                )}
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  })}
                </div>
                <div className={styles.imgSection}>
                  <Image src={`/img/${productFeatures[selectedIndex].mainImg}`}  alt='AI-powered Vehicle Inspection Solution' width={575} height={744} priority/>
                </div>
              </div>
            </div>
        </section>

        {/* Fold 3 — Proof / Impact */}
        <section className={styles.achievementSection}>
          <div className={styles.achievementSubSection}>
            <div className={styles.achievementHeadingContainer}>
              <h2 className={styles.achievementHeading}>Our Impact in Vehicle Inspections</h2>
              <p className={styles.achievementSubHeading}>The numbers reflect the scale of our work and the role Inspektlabs plays in helping businesses assess vehicle condition.</p>
            </div>
            <div className={styles.statisticsContainer}>
              <div className={styles.statisticsContent}>
                <h3 className={styles.statisticsFigure}>10M+</h3>
                <p className={styles.statisticsText}>Inspections Conducted</p>
              </div>
              <div className={styles.statisticsContent}>
                <h3 className={styles.statisticsFigure}>30+</h3>
                <p className={styles.statisticsText}>Countries Reached</p>
              </div>
              <div className={styles.statisticsContent}>
                <h3 className={styles.statisticsFigure}>$1B+</h3>
                <p className={styles.statisticsText}>Inspection Costs Saved</p>
              </div>
            </div>
            <div>
              <p className={styles.statisticsDescription}>Assess vehicle condition from visual evidence and turn inspection data into a consistent, usable format for business workflows.</p>
            </div>
          </div>
        </section>

        {/* Fold 4 — Industries */}
        <section className={styles.targetAudienceSection}>
          <div className={styles.targetAudienceDiv}>
            <h2 className={styles.targetAudienceTitle}>One AI Inspection Platform. Multiple Automotive Workflows.</h2>
            <p className={styles.targetAudienceText}>Every vehicle inspection serves a different purpose. Choose the workflow that fits your business.</p>

            <div className={styles.targetAudienceContainer}>
            {usersCard.map((card) => (
              <div className={styles.cardItem} key={card.userType}>
                <div className={styles.cardImg}>
                  <Image
                    src={`/img/${card.cardImg}`}
                    layout='fill'
                    objectFit='cover'
                    objectPosition='center'
                    alt={card.altText || card.userType}
                  />
                </div>
                <div className={styles.cardContent}>
                  <h3 className={styles.userType}>{card.userType}</h3>
                  <p className={styles.cardText}>{card.text}</p>
                  {card.link && (
                    <Link href={card.link}>
                      <a className={styles.cardCta}>{card.cta} →</a>
                    </Link>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>
        </section>

        {/* Fold 5 — Customers / testimonials */}
        <section className={styles.testimonialSection}>
          <div className={styles.testimonialSubSection}>
            <div className={styles.testimonialsTitleContainer}>
              <h2 className={styles.testimonialsTitle}>Vehicle Inspections Built Around Real Business Needs</h2>
            </div>
            <div className={styles.testimonialsContainer}>
            <div className={styles.testimonialWrapper}>
                <div className={`${styles.testimonialRow} ${styles.row1}`}>
                  <div className={styles.scrollTrack}>
                    {[
                      ...testimonials.slice(0, 5),
                      ...testimonials.slice(0, 5),
                    ].map((t, i) => (
                      <div className={styles.testimonialCard} key={`row1-${i}`}>
                        <div className={styles.testimonialCardHeadingContainer}>
                          <div className={styles.testimonialImageWrapper}>
                            <Image className={`${styles.testimonialImg} ${t.flip=='true' ? styles.flipImg : ''}`} src={`/img/clients/${t.img}`} width={70} height={70} alt={`${t.altName}-Inspektlabs Testimonial`} />
                          </div>
                          <div className={styles.testimonialCardHeading}>
                            <p><strong>{t.name}</strong></p>
                            <p className={styles.testimonialTag}>{t.position}</p>
                          </div>
                        </div>
                        <p className={styles.testimonialText}>“{t.text}”</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`${styles.testimonialRow} ${styles.row2}`}>
                  <div className={styles.scrollTrackReverse}>
                    {[
                      ...testimonials.slice(5),
                      ...testimonials.slice(5),
                    ].map((t, i) => (
                      <div className={`${styles.testimonialCard} ${styles.cardRow2}`} key={`row2-${i}`}>
                       <div className={styles.testimonialCardHeadingContainer}>
                       <div className={styles.testimonialImageWrapper}>
                            <Image className={`${styles.testimonialImg} ${t.flip=='true' ? styles.flipImg : ''}`} src={`/img/clients/${t.img}`} width={60} height={60} alt={`${t.altName}-Inspektlabs Testimonial`} />
                          </div>
                          <div className={styles.testimonialCardHeading}>
                            <p><strong>{t.name}</strong></p>
                            <p>{t.position}</p>
                          </div>
                        </div>
                        <p className={styles.testimonialText}>“{t.text}”</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.testimonialMobileWrapper}>
                  <div className={styles.testimonialRow}>
                    {
                      [...testimonials, ...testimonials].map((t,i) =>(
                        <div className={styles.testimonialCard} key={i}>
                        <div className={styles.testimonialCardHeadingContainer}>
                        <div className={styles.testimonialImageWrapper}>
                            <Image className={`${styles.testimonialImg} ${t.flip=='true' ? styles.flipImg : ''}`} src={`/img/clients/${t.img}`} width={50} height={50} alt={`${t.altName}-Inspektlabs Testimonial`} />
                          </div>
                          <div className={styles.testimonialCardHeading}>
                            <p><strong>{t.name}</strong></p>
                            <p className={styles.testimonialTag}>{t.position}</p>
                          </div>
                        </div>
                        <p>“{t.text}”</p>
                      </div>
                      ))
                    }
                  </div>
              </div>
              {partners.map((category) => (
                <Fragment key={category.name[locale]}>
                  <div className={styles.categoryHeading}>
                    <p className={styles.partnerCategory}>{category.name[locale]}</p>
                    <Image src={'/img/star.svg'} alt='star' width={20} height={20} className={styles.starImg}/>
                  </div>
                  <div className={styles.partnerLogoContainer}>
                    {category.logo.map((t,i) => (
                      <div className={styles.partnerLogo} key={i}>
                        <Image
                          key={t.img}
                          src={`/img/${t.img}`}
                          alt={`${t.name}-Inspektlabs technology partner`}
                          width={t.width? t.width:150}
                          height={t.height? t.height:40}
                          objectFit='contain'
                          className={`${styles.partnerLogoImg} ${t.flip=='true' ? styles.flipImg : ''}`}
                        />
                      </div>
                    ))}
                  </div>
                </Fragment>
              ))}

            </div>
          </div>
        </section>

        {/* Fold 6a — Markets / Our Presence */}
        <section className={styles.mapSection}>
          <div className={styles.marketsHeader}>
            <h2 className={styles.marketsHeading}>Vehicle Inspection Workflows Across Global Markets</h2>
            <p className={styles.marketsIntro}>Support vehicle inspections across different markets, workflows, and operational requirements with a single inspection platform.</p>
          </div>
          <div className={styles.mapContainer}>
              <div className={styles.mapWrapper}>
                <Image
                  src='/img/presence-map.svg'
                  layout='fill'
                  objectFit='contain'
                  objectPosition='center'
                  alt='Inspektlabs global presence — countries served across Europe, the Middle East, LATAM, and the US'
                />
              </div>
          </div>
          <div className={styles.legendsContainer}>
            <div className={styles.legend}>
              <Image
                src='/img/Subsidiaries.svg'
                alt='Subsidiaries'
                width={20}
                height={24}
              />
              <p>{language['Subsidiaries'][locale]}</p>
            </div>
            <div className={styles.legend}>
              <Image
                src='/img/Partners.svg'
                alt='Partners'
                width={20}
                height={24}
              />
              <p>{language['Partners'][locale]}</p>
            </div>
            <div className={styles.legend}>
              <Image
                src='/img/Clients.svg'
                alt='Clients'
                width={20}
                height={24}
              />
              <p>{language['Clients'][locale]}</p>
            </div>
          </div>
        </section>

        {/* Fold 6b — Resources / Blog */}
        <section className={styles.blogSection}>
          <div className={styles.blogTitleContainer}>
            <h2 className={`${styles.testimonialsTitle} ${styles.blogTitleOneLine}`}>Vehicle Inspection Trends & Insights</h2>
            <p className={styles.blogSubtitle}>Explore practical insights on vehicle inspections, damage assessment and the changing needs of automotive businesses.</p>
          </div>
            <div className={styles.blogCaraouselContainer}>
                <div className={`${styles.nav} ${styles.navLeft}`}>
                  <Image src={'/img/nav-left-icon.svg'} width={50} height={50} alt="Previous blog post" onClick={() => scroll("left")}/>
                </div>
                <div className={styles.blogCaraouselContent} ref={scrollRef}>
                  {posts.map((post) => (
                  <div className={styles.blogItem} key={post.id}>
                    <div className={styles.blogImg}>
                      <Image
                        src={post.feature_image}
                        layout='fill'
                        objectFit='cover'
                        objectPosition='center'
                        alt={post.title}
                      />
                    </div>
                    <div className={styles.blogContent}>
                      {post.primary_tag && (
                        <a href={post.primary_tag.url} className={styles.blogTag}>
                          {post.primary_tag.name}
                        </a>
                      )}
                      <p className={styles.blogTitle}>{post.title}</p>
                      <p className={styles.blogText}>{post.excerpt}</p>
                      <a
                        href={post.url}
                        className={styles.blogAction}
                        title={post.title}
                      >
                        {language['Read More'][locale]}
                        <span className='sr-only'>about {post.title}</span>{' '}
                        <ChevronRightIcon />
                      </a>
                    </div>
                    </div>
                  ))}
                </div>
                <div className={`${styles.nav} ${styles.navRight}`}>
                  <Image src={'/img/nav-right-icon.svg'} width={50} height={50} alt="Next blog post" onClick={() => scroll("right")}/>
                </div>
            </div>

        </section>

        {/* Fold 7 — FAQ */}
        <section className={styles.homeFaqSection}>
          <div className={styles.homeFaqBox}>
            <div className={styles.homeFaqHeading}>
              <h2 className={styles.homeFaqTitle}>Frequently Asked Questions About AI Vehicle Inspection Software</h2>
            </div>
            <div className={styles.homeFaqContainer}>
                {homeFaqs.map((item, index)=><QuestionSection key={index} keyIndex={index} que={item.Q} ans={item.A}/>)}
            </div>
            <div
              className={styles.viewMoreBtn}
              style={{width: "200px", margin: "28px auto 0"}}
              onClick={() => (window.location.href = '/faq')}
            >
              View More
            </div>
          </div>
        </section>

        {/* Final CTA banner */}
        <section className={styles.finalCtaSection}>
          <div className={styles.finalCtaInner}>
            <h2 className={styles.finalCtaTitle}>Ready to Automate Your Vehicle Inspection Process?</h2>
            <p className={styles.finalCtaText}>See how Inspektlabs can help your team detect vehicle damage, generate structured inspection reports and streamline vehicle assessment workflows with AI. Discuss your requirements with our team and explore an inspection workflow suited to your business.</p>
            <ContactUsCTA as="div" cluster="homepage-footer" className={styles.finalCtaBtn}>
              Request a Demo
            </ContactUsCTA>
          </div>
        </section>

      </main>
    </div>
  );
}
